import React, {useState, useMemo, useEffect} from 'react';
import {Search} from 'semantic-ui-react';
import {useNavigate} from 'react-router-dom';
import {ROUTE_CATALOG} from '../../App';
import {getViewingConditions} from '../../ProtectedRoute';
import {listVideoContent} from '../VideoLibrary/videoContentService';
import {getFormChecklists} from '../FormChecklists/formChecklistsService';
import { getUsername, fetchWithAuth, getCSRFToken } from '../auth';

const LAZY_SEARCH_DEBOUNCE_MS = 300;

const fetchJson = async (endpoint) => {
    const resp = await fetchWithAuth(
        `${process.env.REACT_APP_ICC_TOOL_API_HOST}${endpoint}`,
        {headers: {'X-CSRFToken': getCSRFToken()}, credentials: 'include'}
    );
    if (!resp.ok) throw new Error(`Request failed: ${endpoint}`);
    return resp.json();
};

const getRouteBreadcrumb = (path) => {
    const entry = ROUTE_CATALOG.find(r => r.path === path);
    if (!entry) return '';
    return (entry.trail || []).filter(t => t && t !== 'Home').join(' > ');
};


const SearchBar = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [extraSource, setExtraSource] = useState([]);
    const [lazyMatches, setLazyMatches] = useState([]);
    const [lazyLoading, setLazyLoading] = useState(false);

    // Videos and form checklists are individual DB records, not static
    // routes, so they can't come from ROUTE_CATALOG. Fetch them once on
    // mount (gated by the same permissions their container pages use) and
    // merge into the searchable source below.
    useEffect(() => {
        const viewingConditions = getViewingConditions();
        const canSeeVideos = viewingConditions.forms;
        const canSeeChecklists = viewingConditions.documents;

        const fetchers = [];

        if (canSeeVideos) {
            const videoCrumb = getRouteBreadcrumb('/documents/video-content');
            fetchers.push(
                listVideoContent('', 1, 1000)
                    .then(data => (data.results || []).map(v => ({
                        title: v.title,
                        description: videoCrumb ? `${videoCrumb} > ${v.title}` : v.title,
                        path: `/documents/video-content?q=${encodeURIComponent(v.title)}`,
                        searchBlob: [v.title, v.description, ...(v.tags || [])]
                            .filter(Boolean).join(' ').toLowerCase(),
                    })))
                    .catch(err => {
                        console.error('SearchBar: failed to load videos', err);
                        return [];
                    })
            );
        }

        if (canSeeChecklists) {
            const checklistCrumb = getRouteBreadcrumb('/documents/form-checklists');
            fetchers.push(
                getFormChecklists()
                    .then(data => (data || []).map(c => ({
                        title: c.name,
                        description: checklistCrumb ? `${checklistCrumb} > ${c.name}` : c.name,
                        path: `/documents/form-checklists/${c.id}`,
                        searchBlob: [c.name, c.activity, c.condition, c.standard]
                            .filter(Boolean).join(' ').toLowerCase(),
                    })))
                    .catch(err => {
                        console.error('SearchBar: failed to load form checklists', err);
                        return [];
                    })
            );
        }

        if (fetchers.length === 0) return;

        Promise.all(fetchers).then(results => {
            setExtraSource(results.flat());
        });
    }, []);

    const source = useMemo(() => {
        const viewingConditions = getViewingConditions();
        
        const hardcodedMatches = [
            {
                title: 'Absences',
                description: 'View a list of all member\'s absences.',
                path: '/events',
                searchBlob: 'absences /events',
            },
            {
                title: 'Monthly Stand-Down',
                description: 'Create, Edit and View stand-down.',
                path: '/standdown',
                searchBlob: 'monthly stand-down standdown /standdown',
            },
            {
                title: 'Profile',
                description: 'Your member page and schedule.',
                path: `/members/${getUsername()}/schedule`,
                searchBlob: 'profile member schedule',
            },
        ];

        const built = ROUTE_CATALOG
            .filter(r => !r.hasParams)
            .filter(r => !r.permission || viewingConditions[r.permission])
            .map(r => {
                const crumbs = (r.trail || []).filter(t => t && t !== 'Home');
                const description = crumbs.join(' > ');
                return {
                    title: r.title,
                    description,
                    tags: r.tags || [],
                    path: r.path,
                    searchBlob: [r.title, description, r.path, ...(r.tags || [])].join(' ').toLowerCase(),
                };
            });
        return [...built, ...hardcodedMatches];
    }, []);

    // Teams and documents are looked up lazily: debounce the typed query,
    // then ask the API. Results are also filtered client-side by name so
    // it works even if the backend ignores the search param.
    useEffect(() => {
        const q = (query || '').trim().toLowerCase();
        const viewingConditions = getViewingConditions();
        const lookups = [];

        if (q && viewingConditions.teams) {
            const crumb = getRouteBreadcrumb('/teams');
            lookups.push(async () => {
                const data = await fetchJson(`teams/?name=${encodeURIComponent(q)}`);
                return (Array.isArray(data) ? data : [])
                    .filter(t => (t.name || '').toLowerCase().includes(q))
                    .map(t => ({
                        title: t.name,
                        description: crumb ? `${crumb} > ${t.name}` : t.name,
                        path: `/teams/${t.id}/view`,
                        searchBlob: t.name.toLowerCase(),
                    }));
            });
        }

        if (q && viewingConditions.forms) {
            const crumb = getRouteBreadcrumb('/documents/list');
            lookups.push(async () => {
                const data = await fetchJson(`documents/?filename=${encodeURIComponent(q)}`);
                return ((data && data.documents) || [])
                    .filter(d => (d.filename || '').toLowerCase().includes(q))
                    .map(d => ({
                        title: d.filename,
                        description: crumb ? `${crumb} > ${d.filename}` : d.filename,
                        path: `/documents/list?q=${encodeURIComponent(d.filename)}`,
                        searchBlob: d.filename.toLowerCase(),
                    }));
            });
        }

        if (lookups.length === 0) {
            setLazyMatches([]);
            setLazyLoading(false);
            return;
        }

        let cancelled = false;
        setLazyLoading(true);
        const timer = setTimeout(async () => {
            const settled = await Promise.all(lookups.map(run => run().catch(err => {
                console.error('SearchBar: lazy lookup failed', err);
                return [];
            })));
            if (cancelled) return;
            setLazyMatches(settled.flat());
            setLazyLoading(false);
        }, LAZY_SEARCH_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            setLazyLoading(false);
            clearTimeout(timer);
        };
    }, [query]);

    const handleSearchChange = (e, {value}) => {
        setQuery(value);
    };

    // Recompute results whenever the query changes OR extraSource arrives
    // late (videos/checklists are fetched async and may resolve after the
    // user has already started typing).
    useEffect(() => {
        const q = (query || '').trim().toLowerCase();
        if (!q) {
            setResults([]);
            return;
        }

        const filtered = [...source, ...extraSource, ...lazyMatches]
            .filter(item => item.searchBlob.includes(q))
            .slice(0, 10)
            .map((item, i) => ({
                title: item.title,
                description: item.description,
                path: item.path,
                key: `${item.path}-${i}`,
            }));

        setResults(filtered);
    }, [query, source, extraSource, lazyMatches]);

    const handleResultSelect = (e, {result}) => {
        if (!result || !result.path) return;
        setQuery('');
        setResults([]);
        navigate(result.path);
    };

    return (
        <Search
            input={{fluid: true, icon: 'search', placeholder: 'Search routes, tags, titles...'}}
            fluid
            loading={lazyLoading}
            value={query}
            onSearchChange={handleSearchChange}
            onResultSelect={handleResultSelect}
            results={results}
            minCharacters={1}
            noResultsMessage='No matches'
        />
    );
}

export default SearchBar;
